import { useState } from "preact/hooks";
import { toast } from "react-hot-toast";
import { apiUrl, instagramWebhookPath } from "../../constants.ts";
import {
  Button,
  Collapsible,
  CopyableValue,
  inputClass,
  PageSpinner,
  TokenDisplay,
} from "./components.tsx";
import { transact, tx, useQuery } from "./db.ts";

export const InstagramConfig = ({ id }: { id: string }) => {
  const [newPageAccessToken, setNewPageAccessToken] = useState("");
  const [newVerifyToken, setNewVerifyToken] = useState("");
  const [newAppSecret, setNewAppSecret] = useState("");
  const { data, error, isLoading } = useQuery({
    bots: { $: { where: { id } } },
  });
  if (isLoading) return <PageSpinner />;
  if (error) return <div>Error: {error.message}</div>;
  const { bots } = data;
  const [bot] = bots;
  const {
    instagramPageAccessToken,
    instagramVerifyToken,
    instagramAppSecret,
  } = bot || {};

  if (!bot) return <div>No bot found</div>;

  const webhookUrl = [apiUrl, instagramWebhookPath, bot.id].join(
    "/",
  );

  return (
    <Collapsible title="Instagram DM">
      <div className="flex flex-col gap-2">
        <div className="mb-4 text-sm text-text-tertiary-light dark:text-text-tertiary">
          <p className="mb-2 font-medium">Setup Instructions:</p>
          <ol className="list-decimal ml-5 space-y-1">
            <li>Create a Facebook App in the Meta for Developers console</li>
            <li>
              Add the Instagram Graph API and Messenger products to your app
            </li>
            <li>
              Link your Instagram Professional Account (Business or Creator) to
              a Facebook Page
            </li>
            <li>
              Generate a Page Access Token for that connected Facebook Page,
              then exchange it for a long-lived (never-expiring) token using
              Meta's{" "}
              <a
                href="https://developers.facebook.com/docs/facebook-login/guides/access-tokens/get-long-lived/"
                target="_blank"
                rel="noopener noreferrer"
                className="underline"
              >
                long-lived token flow
              </a>
              . Short-lived tokens expire in ~1 hour and the bot will stop
              replying.
            </li>
            <li>
              Copy the App Secret from Meta App Dashboard → Settings → Basic.
              This is required so we can verify Meta's webhook signatures —
              without it, any unsigned POST to your webhook URL would be
              accepted.
            </li>
            <li>Enter all three values below and create a verify token</li>
            <li>
              In the Instagram Settings of the App Dashboard, add the webhook
              URL below
            </li>
            <li>
              Subscribe to the "instagram_messaging" and "messaging_postbacks"
              webhook fields under the Instagram Webhook type
            </li>
            <li>Complete webhook verification using your verify token</li>
          </ol>
        </div>

        <div className="mb-4 text-sm text-text-tertiary-light dark:text-text-tertiary mb-2 flex flex-col gap-2">
          <CopyableValue
            value={webhookUrl}
            label="Your webhook should point to"
          />
          <div className="text-xs">
            Webhook fields needed: "instagram_messaging", "messaging_postbacks"
          </div>
        </div>
        {instagramVerifyToken && (
          <div className="mb-4 text-sm max-w-md">
            <TokenDisplay
              label="Verify Token"
              token={instagramVerifyToken}
            />
          </div>
        )}
        {instagramAppSecret && (
          <div className="mb-4 text-sm max-w-md">
            <TokenDisplay
              label="App Secret"
              token={instagramAppSecret}
            />
          </div>
        )}
        {!instagramPageAccessToken && (
          <>
            <div className="mb-4 max-w-md">
              <label>Page Access Token</label>
              <input
                value={newPageAccessToken}
                onChange={(e) => setNewPageAccessToken(e.currentTarget.value)}
                className={inputClass}
                placeholder="Enter your Meta Page Access Token"
              />
            </div>
            <div className="mb-4 max-w-md">
              <label>Verify Token</label>
              <input
                value={newVerifyToken}
                onChange={(e) => setNewVerifyToken(e.currentTarget.value)}
                className={inputClass}
                placeholder="Create a verify token (any string)"
              />
            </div>
            <div className="mb-4 max-w-md">
              <label>App Secret</label>
              <input
                value={newAppSecret}
                onChange={(e) => setNewAppSecret(e.currentTarget.value)}
                className={inputClass}
                placeholder="Paste your Meta App Secret"
              />
            </div>
            <Button
              className="w-fit"
              onClick={() => {
                if (
                  !newPageAccessToken || !newVerifyToken || !newAppSecret
                ) {
                  toast.error(
                    "Page Access Token, Verify Token and App Secret are all required.",
                  );
                  return;
                }
                toast.promise(
                  transact([
                    tx.bots[id].update({
                      instagramPageAccessToken: newPageAccessToken,
                      instagramVerifyToken: newVerifyToken,
                      instagramAppSecret: newAppSecret,
                    }),
                  ]),
                  {
                    loading: "Saving...",
                    success: () => {
                      setNewPageAccessToken("");
                      setNewVerifyToken("");
                      setNewAppSecret("");
                      return "Instagram DM configured!";
                    },
                    error: (err) => `Error: ${err.message}`,
                  },
                );
              }}
            >
              Save
            </Button>
          </>
        )}
        {instagramPageAccessToken && !instagramAppSecret && (
          <div className="mb-4 max-w-md flex flex-col gap-2">
            <div className="text-sm text-warning">
              Missing App Secret. Inbound webhooks will be rejected until this
              is set.
            </div>
            <label>App Secret</label>
            <input
              value={newAppSecret}
              onChange={(e) => setNewAppSecret(e.currentTarget.value)}
              className={inputClass}
              placeholder="Paste your Meta App Secret"
            />
            <Button
              className="w-fit"
              onClick={() => {
                if (!newAppSecret) {
                  toast.error("App Secret is required.");
                  return;
                }
                toast.promise(
                  transact([
                    tx.bots[id].update({
                      instagramAppSecret: newAppSecret,
                    }),
                  ]),
                  {
                    loading: "Saving...",
                    success: () => {
                      setNewAppSecret("");
                      return "App Secret saved!";
                    },
                    error: (err) => `Error: ${err.message}`,
                  },
                );
              }}
            >
              Save App Secret
            </Button>
          </div>
        )}
        {instagramPageAccessToken && (
          <div className="mb-4 text-sm max-w-md">
            <TokenDisplay
              label="Page Access Token"
              token={instagramPageAccessToken}
            />
            <Button
              className="mt-2 bg-danger hover:bg-danger-hover w-fit"
              onClick={() => {
                toast.promise(
                  transact([
                    tx.bots[id].update({
                      instagramPageAccessToken: null,
                      instagramVerifyToken: null,
                      instagramAppSecret: null,
                    }),
                  ]),
                  {
                    loading: "Removing...",
                    success: "Instagram DM disconnected",
                    error: "Failed to disconnect",
                  },
                );
              }}
            >
              Disconnect
            </Button>
          </div>
        )}
      </div>
    </Collapsible>
  );
};
